import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './PartnerMeetingDisplay.css';
import { getTodayYYYYMMDD } from '../../utils/dateUtils';

const DEFAULT_PARTNERS = [
  'Rajkumar Bande',
  'Siddamma Biradar',
  'Akash Bande',
  'Sharanu Alange',
];

const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL.trim()) ||
  (typeof window !== 'undefined' ? window.location.origin : '');

const FALLBACK_API_BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';

const PartnerMeetingDisplay = () => {
  const [availablePartners, setAvailablePartners] = useState(DEFAULT_PARTNERS);
  const [startDate, setStartDate] = useState(getTodayYYYYMMDD());
  const [endDate, setEndDate] = useState(getTodayYYYYMMDD());
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const normalizeKey = useCallback((value) => {
    return String(value || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '');
  }, []);

  const partnersList = useMemo(() => {
    const deduped = new Set();
    return availablePartners
      .map((name) => (typeof name === 'string' ? name.trim() : ''))
      .filter((name) => {
        if (!name) {
          return false;
        }
        const key = name.toUpperCase();
        if (deduped.has(key)) {
          return false;
        }
        deduped.add(key);
        return true;
      });
  }, [availablePartners]);

  const fetchData = useCallback(async (partnersOverride) => {
    setLoading(true);
    setError('');

    const partnersToFetch = Array.isArray(partnersOverride) ? partnersOverride : partnersList;

    if (partnersToFetch.length === 0) {
      setLoading(false);
      setError('Please enter at least one partner name.');
      return;
    }

    const isRetryableNetworkError = (err) => {
      const status = err?.response?.status;
      // 502/503/504 are common when api.* reverse-proxy is down; ERR_NETWORK covers CORS/blocked.
      return (
        status === 502 ||
        status === 503 ||
        status === 504 ||
        err?.code === 'ERR_NETWORK' ||
        err?.message === 'Network Error'
      );
    };

    const runFetch = async (baseUrl) => {
      return axios.post(`${baseUrl}/fetch-data`, {
        partners: partnersToFetch,
        startDate,
        endDate,
      });
    };

    try {
      let baseResult = await runFetch(API_BASE_URL);

      // If API host is down, retry once against same-origin as a fallback.
      if (
        FALLBACK_API_BASE_URL &&
        FALLBACK_API_BASE_URL !== API_BASE_URL &&
        isRetryableNetworkError(baseResult)
      ) {
        console.warn('Primary API failed; retrying against same-origin fallback...');
        baseResult = await runFetch(FALLBACK_API_BASE_URL);
      }

      const baseResponse = baseResult;
      const baseRows = baseResponse?.data?.data || baseResponse?.data || [];

      const merged = (Array.isArray(baseRows) ? baseRows : []).map((row) => {
        const key = normalizeKey(row?.partnerName);
        const cd = Number(row?.cd || 0);
        const od = Number(row?.od || 0);
        const gl = Number(row?.gl || 0);
        const tbd = Number(row?.tbd || 0);
        const dc = Number(row?.dc || 0);
        return {
          ...row,
          cd,
          total: od + gl + cd + tbd + dc,
        };
      });

      setTableData(merged);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [partnersList, startDate, endDate, normalizeKey]);

  useEffect(() => {
    setAvailablePartners(DEFAULT_PARTNERS);
  }, []);

  // Note: fetch is now manual via the button to avoid continuous auto-loading

  const toNumber = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const calculateRowTotal = (row) =>
    toNumber(row.od) +
    toNumber(row.gl) +
    toNumber(row.cd) +
    toNumber(row.tbd) +
    toNumber(row.dc);

  const basePartners = useMemo(() => {
    if (partnersList.length > 0) {
      return partnersList;
    }
    if (availablePartners.length > 0) {
      return availablePartners;
    }
    return DEFAULT_PARTNERS;
  }, [partnersList, availablePartners]);

  const displayRows = useMemo(() => {
    const partnerKeys = new Set(basePartners.map((name) => name.trim().toUpperCase()));
    const dataMap = new Map();

    tableData.forEach((row) => {
      const key = row.partnerName ? row.partnerName.trim().toUpperCase() : '';
      if (key) {
        dataMap.set(key, row);
      }
    });

    const partnerRows = basePartners.map((name) => {
      const key = name.trim().toUpperCase();
      const existingRow = dataMap.get(key);
      if (existingRow) {
        return existingRow;
      }
      return {
        partnerName: name,
        od: 0,
        gl: 0,
        cd: 0,
        tbd: 0,
        dc: 0,
      };
    });

    const extraRows = tableData.filter((row) => {
      const key = row.partnerName ? row.partnerName.trim().toUpperCase() : '';
      return key && !partnerKeys.has(key);
    });

    return [...partnerRows, ...extraRows];
  }, [basePartners, tableData]);

  const calculateGrandTotal = (rows) => {
    const totals = { od: 0, gl: 0, cd: 0, tbd: 0, dc: 0, total: 0 };

    rows.forEach((row) => {
      totals.od += toNumber(row.od);
      totals.gl += toNumber(row.gl);
      totals.cd += toNumber(row.cd);
      totals.tbd += toNumber(row.tbd);
      totals.dc += toNumber(row.dc);
      totals.total += calculateRowTotal(row);
    });

    return totals;
  };

  const grandTotal = useMemo(() => calculateGrandTotal(displayRows), [displayRows]);

  const handlePrint = () => {
    const printContent = document.getElementById('table-container').innerHTML;
    const printWindow = window.open('', '_blank');
    const partnersJoined = partnersList.join(', ') || '—';
    printWindow.document.write(`
      <html>
        <head>
          <title>Partnetwise Interest Meeting Data</title>
          <style>
            body { font-family: Arial, sans-serif; }
            h2, h4 { margin: 0 0 8px; }
            .meta { margin: 8px 0 16px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 16px; }
            th, td { border: 1px solid #ccc; padding: 10px; text-align: left; }
            th { background-color: #f4f4f4; font-weight: bold; }
            .font-bold { font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Partnerwise Interest Meeting Data</h2>
          <div class="meta">
            <h4>Date Range: ${startDate} to ${endDate}</h4>
           
          </div>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="container">
      <h2 className="title">Partner Meeting Data</h2>

      <div className="controls">
        <div className="date-input">
          <label>Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="date-input">
          <label>End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <button onClick={() => fetchData()} className="fetch-button">Fetch Data</button>
      </div>

      {loading ? (
        <div className="loading">Loading data...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div id="table-container" className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Partner Name</th>
                <th>OD</th>
                <th>GL</th>
                <th>CD</th>
                <th>TBD</th>
                <th>DC</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, index) => (
                <tr key={index}>
                  <td>{row.partnerName || 'N/A'}</td>
                  <td>{toNumber(row.od).toFixed(2)}</td>
                  <td>{toNumber(row.gl).toFixed(2)}</td>
                  <td>{toNumber(row.cd).toFixed(2)}</td>
                  <td>{toNumber(row.tbd).toFixed(2)}</td>
                  <td>{toNumber(row.dc).toFixed(2)}</td>
                  <td>{calculateRowTotal(row).toFixed(2)}</td>
                </tr>
              ))}

              {/* Grand Total Row */}
              <tr className="font-bold">
                <td>Grand Total</td>
                <td>{grandTotal.od.toFixed(2)}</td>
                <td>{grandTotal.gl.toFixed(2)}</td>
                <td>{grandTotal.cd.toFixed(2)}</td>
                <td>{grandTotal.tbd.toFixed(2)}</td>
                <td>{grandTotal.dc.toFixed(2)}</td>
                <td>{grandTotal.total.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <button onClick={handlePrint} className="print-button">Print Table</button>
    </div>
  );
};

export default PartnerMeetingDisplay;
